import React, { RefObject, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  AlignJustify,
  AlignLeft,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Hand,
  Loader2,
  MessageSquare,
  Minus,
  Plus,
  Ruler,
  Sparkles,
  Type,
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import type {
  DocumentSelectionMode,
  ResponseStyle,
  ResponseLength,
} from '../../../types/documentSelection';
import { eventBus, EventNames } from '../../../utils/eventBus';
import ConfigSummaryChips from './ConfigSummaryChips';
import type { ChipDef, ChipOption, SubmenuTarget } from './ConfigSummaryChips';

/**
 * Barra de herramientas del input
 *
 * Botón "+" abre el panel de documentos.
 * Config chips inline muestran y permiten ajustar: Modelo, Fiabilidad, Estilo, Longitud, Features.
 */

const MODE_CONFIG: Record<DocumentSelectionMode, { label: string; icon: typeof Sparkles }> = {
  smart: { label: 'Smart', icon: Sparkles },
  manual: { label: 'Manual', icon: Hand },
};

const STYLE_CONFIG: Record<ResponseStyle, { label: string; icon: typeof Type }> = {
  normal: { label: 'Normal', icon: Type },
  concise: { label: 'Conciso', icon: AlignLeft },
  detailed: { label: 'Detallado', icon: AlignJustify },
  technical: { label: 'Técnico', icon: Briefcase },
  simple: { label: 'Simple', icon: MessageSquare },
};

const LENGTH_CONFIG: Record<ResponseLength, { label: string; icon: typeof Ruler }> = {
  auto: { label: 'Auto', icon: Minus },
  short: { label: 'Corta', icon: AlignLeft },
  long: { label: 'Larga', icon: AlignJustify },
};

const TEMPERATURE_PRESETS = [
  { id: 'deterministic', label: 'Alta', value: 0.0 },
  { id: 'balanced', label: 'Balanceada', value: 0.5 },
  { id: 'creative', label: 'Baja', value: 1.0 },
] as const;

function getTemperatureLabel(temp: number): string {
  const match = TEMPERATURE_PRESETS.find((p) => p.value === temp);
  return match ? match.label : temp.toFixed(1);
}

function getModelShortName(modelId: string, models?: Array<{ id: string; name: string }>): string {
  const model = models?.find((m) => m.id === modelId);
  if (!model) return modelId;
  return model.name.replace(/^GPT-/, '');
}

export interface InputToolbarProps {
  selectedDocumentCount: number;
  /** Total available documents (to distinguish "all selected" from a subset) */
  totalDocumentCount?: number;
  isStreaming: boolean;
  disabled?: boolean;
  onDocumentUpload?: (file: File) => void;
  onDocumentUploadShared?: (file: File) => void;
  fileInputRef?: RefObject<HTMLInputElement>;
  fileInputSharedRef?: RefObject<HTMLInputElement>;
  isAdmin?: boolean;
  documentSelectionMode?: DocumentSelectionMode;
  onModeChange?: (mode: DocumentSelectionMode) => void;
  responseStyle?: ResponseStyle;
  onStyleChange?: (style: ResponseStyle) => void;
  responseLength?: ResponseLength;
  onLengthChange?: (length: ResponseLength) => void;
  currentModel?: string;
  availableModels?: Array<{ id: string; name: string; recommended?: boolean }>;
  onModelChange?: (modelId: string) => void;
  currentTemperature?: number;
  onTemperatureChange?: (temp: number) => void;
  suggestedQuestionsEnabled?: boolean;
  suggestedTasksEnabled?: boolean;
  onSuggestedQuestionsToggle?: (enabled: boolean) => void;
  onSuggestedTasksToggle?: (enabled: boolean) => void;
  /** Optional children rendered inline after the "+" button (e.g. SuggestionIndicator) */
  children?: React.ReactNode;
}

const InputToolbar: React.FC<InputToolbarProps> = ({
  selectedDocumentCount,
  totalDocumentCount,
  isStreaming,
  disabled = false,
  onDocumentUpload,
  onDocumentUploadShared,
  fileInputRef,
  fileInputSharedRef,
  isAdmin = false,
  documentSelectionMode,
  onModeChange,
  responseStyle,
  onStyleChange,
  responseLength,
  onLengthChange,
  currentModel,
  availableModels,
  onModelChange,
  currentTemperature,
  onTemperatureChange,
  suggestedQuestionsEnabled,
  suggestedTasksEnabled,
  onSuggestedQuestionsToggle,
  onSuggestedTasksToggle,
  children,
}) => {
  const { theme } = useTheme();

  // Collapse secondary chips (model, temperature, style, length, features) by default
  const [chipsExpanded, setChipsExpanded] = useState(false);

  // A4: Upload feedback state — event-driven via DOCUMENT_UPLOADED
  const [isUploading, setIsUploading] = useState(false);
  const uploadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFileUpload = useCallback((files: File[], uploadFn: (file: File) => void) => {
    setIsUploading(true);
    files.forEach((file) => uploadFn(file));
    // Safety fallback: clear after 30s if no DOCUMENT_UPLOADED event fires
    if (uploadTimeoutRef.current) clearTimeout(uploadTimeoutRef.current);
    uploadTimeoutRef.current = setTimeout(() => setIsUploading(false), 30000);
  }, []);

  // Clear upload state when document upload completes
  useEffect(() => {
    const handler = () => {
      setIsUploading(false);
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
    };
    eventBus.on(EventNames.DOCUMENT_UPLOADED, handler);
    return () => {
      eventBus.off(EventNames.DOCUMENT_UPLOADED, handler);
      if (uploadTimeoutRef.current) clearTimeout(uploadTimeoutRef.current);
    };
  }, []);

  // Active mode for button label
  const activeMode = documentSelectionMode ? MODE_CONFIG[documentSelectionMode] : null;
  const ActiveIcon = activeMode?.icon;

  // Active model short name for button label
  const activeModelName = currentModel ? getModelShortName(currentModel, availableModels) : null;

  // Active temperature label for button label (always show)
  const activeTemperatureLabel =
    currentTemperature !== undefined ? getTemperatureLabel(currentTemperature) : null;

  // Active style for button label (always show)
  const activeStyle = responseStyle ? STYLE_CONFIG[responseStyle] : null;

  // Active length for button label (always show)
  const activeLength = responseLength ? LENGTH_CONFIG[responseLength] : null;

  // Build chip definitions for ConfigSummaryChips (Category: Value pattern)
  // Chips with `options` will show a mini-popover on click; chips without open menu or panel
  const chips = useMemo<ChipDef[]>(() => {
    const result: ChipDef[] = [];

    // Docs: mode + count — Smart hides count (AI picks), Manual always shows count
    if (activeMode) {
      let docsLabel = `Docs: ${activeMode.label}`;
      if (documentSelectionMode === 'manual') {
        docsLabel += ` (${selectedDocumentCount})`;
      }
      result.push({
        id: 'sources',
        label: docsLabel,
        options: Object.entries(MODE_CONFIG).map(([k, v]) => ({ value: k, label: v.label })),
        activeValue: documentSelectionMode,
      });
    }

    // Model — popover with all available models
    if (activeModelName)
      result.push({
        id: 'model',
        label: `Modelo: ${activeModelName}`,
        options: availableModels?.map((m) => ({ value: m.id, label: m.name })) || [],
        activeValue: currentModel,
      });

    // Temperature — popover with 4 presets
    if (activeTemperatureLabel)
      result.push({
        id: 'temperature',
        label: `Fiab: ${activeTemperatureLabel}`,
        options: TEMPERATURE_PRESETS.map((p) => ({ value: String(p.value), label: p.label })),
        activeValue: currentTemperature !== undefined ? String(currentTemperature) : undefined,
      });

    // Style — popover with 5 styles
    if (activeStyle)
      result.push({
        id: 'style',
        label: `Resp: ${activeStyle.label}`,
        options: Object.entries(STYLE_CONFIG).map(([k, v]) => ({ value: k, label: v.label })),
        activeValue: responseStyle,
      });

    // Length — popover with 3 lengths
    if (activeLength)
      result.push({
        id: 'length',
        label: `Long: ${activeLength.label}`,
        options: Object.entries(LENGTH_CONFIG).map(([k, v]) => ({ value: k, label: v.label })),
        activeValue: responseLength,
      });

    // Features — binary popover (On/Off)
    result.push({
      id: 'features',
      subId: 'questions',
      label: `Preg: ${suggestedQuestionsEnabled !== false ? 'on' : 'off'}`,
      options: [
        { value: 'on', label: 'On' },
        { value: 'off', label: 'Off' },
      ],
      activeValue: suggestedQuestionsEnabled !== false ? 'on' : 'off',
      isStrikethrough: suggestedQuestionsEnabled === false,
    });
    result.push({
      id: 'features',
      subId: 'tasks',
      label: `Tareas: ${suggestedTasksEnabled !== false ? 'on' : 'off'}`,
      options: [
        { value: 'on', label: 'On' },
        { value: 'off', label: 'Off' },
      ],
      activeValue: suggestedTasksEnabled !== false ? 'on' : 'off',
      isStrikethrough: suggestedTasksEnabled === false,
    });

    return result;
  }, [
    activeMode,
    documentSelectionMode,
    selectedDocumentCount,
    totalDocumentCount,
    activeModelName,
    availableModels,
    currentModel,
    activeTemperatureLabel,
    currentTemperature,
    activeStyle,
    responseStyle,
    activeLength,
    responseLength,
    suggestedQuestionsEnabled,
    suggestedTasksEnabled,
  ]);

  // Chip click: cycles to next value (called only when popover is already open)
  const handleChipClick = useCallback(
    (chip: ChipDef) => {
      const { id: target, subId } = chip;

      // Special: docs → Document Panel (unchanged)
      if (target === 'docs') {
        eventBus.emit(EventNames.OPEN_DOCUMENT_PANEL, {
          tab: 'seleccionados' as const,
        });
        return;
      }

      // Cycle: model
      if (target === 'model' && currentModel && availableModels && onModelChange) {
        const idx = availableModels.findIndex((m) => m.id === currentModel);
        const nextIdx = (idx + 1) % availableModels.length;
        onModelChange(availableModels[nextIdx].id);
        return;
      }

      // Toggle: sources (Smart ↔ Manual)
      if (target === 'sources' && documentSelectionMode && onModeChange) {
        onModeChange(documentSelectionMode === 'smart' ? 'manual' : 'smart');
        return;
      }

      // Cycle: temperature (Alta → Balanceada → Baja → Alta...)
      if (target === 'temperature' && currentTemperature !== undefined && onTemperatureChange) {
        const idx = TEMPERATURE_PRESETS.findIndex((p) => p.value === currentTemperature);
        const nextIdx = (idx + 1) % TEMPERATURE_PRESETS.length;
        onTemperatureChange(TEMPERATURE_PRESETS[nextIdx].value);
        return;
      }

      // Cycle: style (Normal → Conciso → Detallado → Técnico → Simple → Normal...)
      if (target === 'style' && responseStyle && onStyleChange) {
        const styles = Object.keys(STYLE_CONFIG) as ResponseStyle[];
        const idx = styles.indexOf(responseStyle);
        const nextIdx = (idx + 1) % styles.length;
        onStyleChange(styles[nextIdx]);
        return;
      }

      // Cycle: length (Auto → Corta → Larga → Auto...)
      if (target === 'length' && responseLength && onLengthChange) {
        const lengths = Object.keys(LENGTH_CONFIG) as ResponseLength[];
        const idx = lengths.indexOf(responseLength);
        const nextIdx = (idx + 1) % lengths.length;
        onLengthChange(lengths[nextIdx]);
        return;
      }

      // Toggle: features (on ↔ off)
      if (target === 'features') {
        if (subId === 'questions' && onSuggestedQuestionsToggle) {
          onSuggestedQuestionsToggle(!suggestedQuestionsEnabled);
        } else if (subId === 'tasks' && onSuggestedTasksToggle) {
          onSuggestedTasksToggle(!suggestedTasksEnabled);
        }
        return;
      }
    },
    [
      documentSelectionMode,
      onModeChange,
      currentModel,
      availableModels,
      onModelChange,
      currentTemperature,
      onTemperatureChange,
      responseStyle,
      onStyleChange,
      responseLength,
      onLengthChange,
      suggestedQuestionsEnabled,
      onSuggestedQuestionsToggle,
      suggestedTasksEnabled,
      onSuggestedTasksToggle,
    ]
  );

  // Popover direct-select: user picks an option from the mini-popover
  const handlePopoverOptionSelect = useCallback(
    (chip: ChipDef, value: string) => {
      const { id: target, subId } = chip;

      if (target === 'sources' && onModeChange) {
        onModeChange(value as DocumentSelectionMode);
      } else if (target === 'model' && onModelChange) {
        onModelChange(value);
      } else if (target === 'temperature' && onTemperatureChange) {
        onTemperatureChange(parseFloat(value));
      } else if (target === 'style' && onStyleChange) {
        onStyleChange(value as ResponseStyle);
      } else if (target === 'length' && onLengthChange) {
        onLengthChange(value as ResponseLength);
      } else if (target === 'features') {
        const enabled = value === 'on';
        if (subId === 'questions' && onSuggestedQuestionsToggle)
          onSuggestedQuestionsToggle(enabled);
        if (subId === 'tasks' && onSuggestedTasksToggle) onSuggestedTasksToggle(enabled);
      }
    },
    [
      onModeChange,
      onModelChange,
      onTemperatureChange,
      onStyleChange,
      onLengthChange,
      onSuggestedQuestionsToggle,
      onSuggestedTasksToggle,
    ]
  );

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      {/* ===== Botón "+" (abre documentos) + chips de config ===== */}
      <div className="relative flex items-center gap-1">
        {/* + button → upload document */}
        <button
          type="button"
          onClick={() => fileInputRef?.current?.click()}
          disabled={disabled || isStreaming || !onDocumentUpload}
          className={`inline-flex items-center justify-center p-1.5 rounded-lg border min-h-[44px] min-w-[44px] ${theme.colors.border.primary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors disabled:opacity-40`}
          title="Subir documento"
          aria-label="Subir documento"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
        </button>

        {/* Config summary chips — primary (sources) always visible, secondary collapsed by default */}
        {(() => {
          const primaryChips = chips.filter((c) => c.id === 'sources');
          const secondaryChips = chips.filter((c) => c.id !== 'sources');
          return (
            <>
              <ConfigSummaryChips
                chips={chipsExpanded ? chips : primaryChips}
                disabled={disabled || isStreaming}
                onChipClick={handleChipClick}
                onOptionSelect={handlePopoverOptionSelect}
              />
              {secondaryChips.length > 0 && (
                <button
                  type="button"
                  onClick={() => setChipsExpanded((prev) => !prev)}
                  disabled={disabled || isStreaming}
                  className={`inline-flex items-center justify-center p-0.5 rounded-lg min-h-[44px] min-w-[44px] transition-all disabled:opacity-40 ${
                    chipsExpanded ? 'opacity-60 hover:opacity-90' : 'opacity-40 hover:opacity-70'
                  }`}
                  title={chipsExpanded ? 'Ocultar configuración' : 'Mostrar configuración'}
                  aria-label={chipsExpanded ? 'Ocultar configuración' : 'Mostrar configuración'}
                >
                  {chipsExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </>
          );
        })()}

        {/* Hidden file inputs (must stay in DOM) */}
        {onDocumentUpload && fileInputRef && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.md"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0 && onDocumentUpload) {
                handleFileUpload(files, onDocumentUpload);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }
            }}
            className="hidden"
          />
        )}
        {isAdmin && onDocumentUploadShared && fileInputSharedRef && (
          <input
            ref={fileInputSharedRef}
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.md"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0 && onDocumentUploadShared) {
                files.forEach((file) => {
                  onDocumentUploadShared(file);
                });
                if (fileInputSharedRef.current) {
                  fileInputSharedRef.current.value = '';
                }
              }
            }}
            className="hidden"
          />
        )}
      </div>

      {/* Inline children (e.g. SuggestionIndicator) */}
      {children}
    </div>
  );
};

export default InputToolbar;
